/**
 * Battery Policy validation for the Battery Configurator admin page (spec §2.4.2).
 *
 * Scope: this policy owns the hardware spec and the voltage calibration, so only two of
 * the spec's ranges are enforceable here —
 *
 *   - batteryVoltage ("batteryVoltageV" in the spec) within 6-800 V
 *   - a monotonic voltage map table ("voltageMapTable")
 *
 * The third rule, crankFailV < crankWarningV < batteryVoltageV, spans two records: the
 * cranking thresholds live on the vehicle preset. It is enforced there, by
 * validateVehicleBatteryDiagnosticFieldValues, which is passed this policy's voltage.
 *
 * Returns { ok, errors } to match fuelPolicyValidation.js.
 */

export const BATTERY_VOLTAGE_MIN_V = 6;
export const BATTERY_VOLTAGE_MAX_V = 800;

const num = (v) => {
    if (v === '' || v === null || v === undefined) return NaN;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
};

const hasFilledValue = (value) => value !== '' && value !== null && value !== undefined;

/* Bands are written high-to-low, so the top band sorts first. */
const sortedByVoltageDesc = (bands) => [...bands].sort((a, b) => b.from - a.from);

/**
 * Voltage map rows are { voltageFrom, voltageTo, percentage } (see component/mapBattery).
 * A row is a band written high-to-low: `voltageFrom` is the top of the band and
 * `voltageTo` the bottom, which is how `getBatteryState` (component/assetDetailsCard)
 * reads it back — `dcVolt <= voltageFrom && dcVolt >= voltageTo`. Note this is the
 * opposite of the ascending fuel map tables in fuelPolicyValidation.js.
 *
 * Bands must tile their range: neither overlapping nor leaving a gap. A gap is the more
 * damaging of the two — `getBatteryState` looks the voltage up with `.find()`, so a
 * reading inside a hole returns no band and the battery indicator silently disappears.
 *
 * Monotonic means percentage never falls as voltage rises — a higher band may never map
 * to a lower state of charge.
 */
export function validateVoltageMapTable(rows) {
    const errs = [];
    if (!Array.isArray(rows) || rows.length < 1) {
        return { ok: false, errors: ['Voltage map table requires at least one row.'] };
    }

    /* Row numbers in the messages point at the table as the user sees it, so the per-row
     * checks run in table order; only the cross-row checks below need sorting. */
    const bands = rows.map((row, i) => {
        const from = num(row.voltageFrom);
        const to = num(row.voltageTo);
        const percentage = num(row.percentage);
        if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(percentage)) {
            // Collect rather than bail, so an empty new row cannot mask a real error.
            errs.push(`Voltage map row ${i + 1}: from, to and percentage must all be numbers.`);
            return null;
        }
        if (from <= to) {
            errs.push(`Voltage map row ${i + 1}: "from" must be greater than "to".`);
        }
        if (percentage < 0 || percentage > 100) {
            errs.push(`Voltage map row ${i + 1}: percentage must be between 0 and 100.`);
        }
        return { from, to, percentage };
    });

    // Comparing bands is only meaningful once every row holds numbers.
    if (bands.some((band) => band === null)) {
        return { ok: false, errors: errs };
    }

    const sorted = sortedByVoltageDesc(bands);

    for (let i = 1; i < sorted.length; i += 1) {
        const bandAbove = sorted[i - 1].to;
        const thisBand = sorted[i].from;
        if (thisBand > bandAbove) {
            errs.push('Voltage map rows must not overlap.');
            break;
        }
        if (thisBand < bandAbove) {
            errs.push(`Voltage map leaves a gap between ${thisBand} V and ${bandAbove} V; readings there map to no battery level.`);
            break;
        }
    }

    for (let i = 1; i < sorted.length; i += 1) {
        if (sorted[i].percentage > sorted[i - 1].percentage) {
            errs.push('Voltage map must be monotonic: a higher voltage cannot map to a lower percentage.');
            break;
        }
    }

    return { ok: errs.length === 0, errors: errs };
}

/** Validate the Battery Configurator form before save. */
export function validateBatteryPolicyForSave(form) {
    const errors = {};

    if (!form.name?.trim()) {
        errors.name = 'Policy name is required.';
    }

    if (hasFilledValue(form.batteryVoltage)) {
        const v = num(form.batteryVoltage);
        if (!Number.isFinite(v)) {
            errors.batteryVoltage = 'Battery voltage must be a valid number.';
        } else if (v < BATTERY_VOLTAGE_MIN_V || v > BATTERY_VOLTAGE_MAX_V) {
            errors.batteryVoltage = `Battery voltage must be between ${BATTERY_VOLTAGE_MIN_V} and ${BATTERY_VOLTAGE_MAX_V} V.`;
        }
    }

    if (hasFilledValue(form.batteryCapacity) && !Number.isFinite(num(form.batteryCapacity))) {
        errors.batteryCapacity = 'Battery capacity must be a valid number.';
    }

    if (form.tableMapEnabled) {
        const result = validateVoltageMapTable(form.voltageMaps);
        if (!result.ok) {
            [errors.voltageMap] = result.errors;
        }
    }

    if (form.equationMapEnabled) {
        const equationMap = form.equationMap || {};
        const badKeys = ['a2', 'a1', 'a0'].filter(
            (key) => hasFilledValue(equationMap[key]) && !Number.isFinite(num(equationMap[key])),
        );
        if (badKeys.length > 0) {
            errors.equationMap = `Equation coefficients must be valid numbers (check ${badKeys.join(', ')}).`;
        }
    }

    return { ok: Object.keys(errors).length === 0, errors };
}
